import { z } from 'zod'
import { StringSchema } from '../../../utils/zodStrings'
import { UuidSchema } from '../../../utils/zodUuid'
import { CommentTextSchema } from './commentSchemas'
import type {
  ConfiguredApprovalsFieldOptions,
  ConfiguredCommentsFieldOptions,
  ConfiguredFieldBaseValue,
  ConfiguredFieldInitialValueContext,
  ConfiguredFieldOptions,
  ConfiguredFieldValue,
  ConfiguredFieldValues,
  ConfiguredFieldValuesInput,
  ConfiguredFieldValuesSchemaContext,
  ConfiguredLongTextFieldOptions,
  ConfiguredPayItemsFieldOptions,
  ConfiguredSelectFieldOptions,
  ConfiguredSelectedByFieldOptions,
} from './configuredFields'

/**
 * Normalizes field UUID keys before matching configuration. Checking original
 * entries preserves collisions that a transforming record key would overwrite.
 */
export const ConfiguredFieldValuesInputSchema = z
  .record(z.string(), z.unknown())
  .transform((values, context) => {
    const normalized: ConfiguredFieldValuesInput = {}
    for (const [key, value] of Object.entries(values)) {
      const result = UuidSchema.safeParse(key)
      if (!result.success) {
        for (const issue of result.error.issues)
          context.addIssue({ ...issue, path: [key, ...issue.path] })
        continue
      }
      if (Object.prototype.hasOwnProperty.call(normalized, result.data)) {
        context.addIssue({
          code: 'custom',
          message: 'Field IDs must not contain duplicate UUIDs',
          path: [key],
        })
        continue
      }
      normalized[result.data] = value
    }
    return normalized
  })

const NormalizedOptionalTextSchema = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  StringSchema.max(4000).nullable()
)

/** Rules shared by select-like configured-field value schemas. */
export type ConfiguredOptionValueSchemaOptions = {
  activeOptionIds: readonly string[]
  allowUnconfiguredOptionIds?: boolean
  required: boolean | 'all'
  retainedOptionIds?: readonly string[]
  single: boolean
}

/**
 * Creates intrinsic selection validation shared by configured fields.
 *
 * Pay items pass `allowUnconfiguredOptionIds` because their UUIDs are checked
 * against project rows after this browser-safe shape validation.
 */
export function createConfiguredOptionValueSchema({
  activeOptionIds,
  allowUnconfiguredOptionIds = false,
  required,
  retainedOptionIds = [],
  single,
}: ConfiguredOptionValueSchemaOptions) {
  const allowedOptionIds = new Set(
    [...activeOptionIds, ...retainedOptionIds].map((id) => id.toLowerCase())
  )
  return z.array(UuidSchema).superRefine((value, context) => {
    /*
     * Client-submitted UUIDs may use any casing, so identity checks and stored
     * values must use the same lowercase representation.
     */
    const selectedIds = new Set(value)
    if (selectedIds.size !== value.length)
      context.addIssue({
        code: 'custom',
        message: 'Options must not contain duplicate IDs',
      })

    if (
      !allowUnconfiguredOptionIds &&
      value.some((id) => !allowedOptionIds.has(id))
    )
      context.addIssue({
        code: 'custom',
        message: 'Options must be active or retained current options',
      })

    if (single && value.length > 1)
      context.addIssue({
        code: 'custom',
        message: 'Only one option may be selected',
      })

    if (required && value.length === 0)
      context.addIssue({ code: 'custom', message: 'Required' })
    else if (
      required === 'all' &&
      activeOptionIds.some((id) => !selectedIds.has(id.toLowerCase()))
    )
      context.addIssue({
        code: 'custom',
        message: 'Every active option is required',
      })
  })
}

function arrayValueSchema(
  field:
    | ConfiguredApprovalsFieldOptions
    | ConfiguredPayItemsFieldOptions
    | ConfiguredSelectFieldOptions,
  activeOptionIds: readonly string[],
  retainedOptionIds: readonly string[],
  validateOptionIds: boolean
) {
  return createConfiguredOptionValueSchema({
    activeOptionIds,
    allowUnconfiguredOptionIds: !validateOptionIds,
    required: field.required,
    retainedOptionIds,
    single: field.single,
  })
}

function longTextValueSchema(field: ConfiguredLongTextFieldOptions) {
  return NormalizedOptionalTextSchema.refine(
    (value) => !field.required || value !== null,
    { message: 'Required' }
  )
}

function commentsValueSchema(field: ConfiguredCommentsFieldOptions) {
  if (!field.optional) return CommentTextSchema
  return z.preprocess(
    (value) =>
      typeof value === 'string' && value.trim() === '' ? null : value,
    CommentTextSchema.nullable()
  )
}

/** Validates a nullable selection against active and retained option identities. */
function selectedByValueSchema(
  field: ConfiguredSelectedByFieldOptions,
  activeOptionIds: readonly string[],
  retainedOptionIds: readonly string[]
) {
  const allowedOptionIds = new Set(
    [...activeOptionIds, ...retainedOptionIds].map((id) => id.toLowerCase())
  )
  return UuidSchema.nullable().superRefine((value, context) => {
    if (value !== null && !allowedOptionIds.has(value))
      context.addIssue({
        code: 'custom',
        message: 'Option must be active or a retained current option',
      })
    if (field.required && value === null)
      context.addIssue({ code: 'custom', message: 'Required' })
  })
}

/** Keeps each parsed value paired with the field settings that validated it. */
function createConfiguredFieldInputSchema(
  field: ConfiguredFieldOptions,
  {
    activeOptionIds,
    retainedOptionIds,
  }: {
    activeOptionIds?: readonly string[]
    retainedOptionIds?: readonly string[]
  } = {}
) {
  let fieldActiveOptionIds: readonly string[] = []
  if ('options' in field)
    fieldActiveOptionIds = field.options
      .filter(({ deleted }) => !deleted)
      .map(({ id }) => id)
  const activeIds = activeOptionIds ?? fieldActiveOptionIds
  const retainedIds = field.type === 'approvals' ? [] : retainedOptionIds ?? []
  if (field.type === 'long_text')
    return longTextValueSchema(field).transform((value) => ({
      type: field.type,
      field,
      value,
    }))
  if (field.type === 'comments')
    return commentsValueSchema(field).transform((value) => ({
      type: field.type,
      field,
      value,
    }))
  if (field.type === 'selected_by')
    return selectedByValueSchema(field, activeIds, retainedIds).transform(
      (value) => ({ type: field.type, field, value })
    )
  if (field.type === 'approvals')
    return arrayValueSchema(field, activeIds, retainedIds, true).transform(
      (value) => ({ type: field.type, field, value })
    )
  if (field.type === 'select')
    return arrayValueSchema(field, activeIds, retainedIds, true).transform(
      (value) => ({ type: field.type, field, value })
    )
  return arrayValueSchema(field, activeIds, retainedIds, false).transform(
    (value) => ({ type: field.type, field, value })
  )
}

type ParsedConfiguredFieldInput = z.output<
  ReturnType<typeof createConfiguredFieldInputSchema>
>

/**
 * Validates a complete action form and returns inputs in configured-field order,
 * retaining the relationship between each field's type, settings, and value.
 * Every configured key is required even when its value is optional. Unknown
 * fields and invalid values report issues at their field-ID paths.
 */
export function createConfiguredFieldInputsSchema(
  fields: readonly ConfiguredFieldOptions[],
  contexts: ConfiguredFieldValuesSchemaContext = {}
) {
  return ConfiguredFieldValuesInputSchema.transform((values, context) => {
    const expectedIds = new Set(fields.map(({ id }) => id))
    const normalized: ParsedConfiguredFieldInput[] = []

    for (const id of Object.keys(values)) {
      if (!expectedIds.has(id))
        context.addIssue({
          code: 'custom',
          message: 'Field is not enabled for this action',
          path: [id],
        })
    }

    for (const field of fields) {
      if (!Object.prototype.hasOwnProperty.call(values, field.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Field value is required',
          path: [field.id],
        })
        continue
      }
      const result = createConfiguredFieldInputSchema(
        field,
        contexts[field.id]
      ).safeParse(values[field.id])
      if (result.success) normalized.push(result.data)
      else
        for (const issue of result.error.issues)
          context.addIssue({ ...issue, path: [field.id, ...issue.path] })
    }
    return normalized
  }) satisfies z.ZodType<
    ParsedConfiguredFieldInput[],
    ConfiguredFieldValuesInput
  >
}

/**
 * Validates a complete action form and returns normalized values keyed by field
 * ID for submission. Uses the same parsing rules as the API's typed inputs.
 */
export function createConfiguredFieldValuesSchema(
  fields: readonly ConfiguredFieldOptions[],
  contexts: ConfiguredFieldValuesSchemaContext = {}
) {
  return createConfiguredFieldInputsSchema(fields, contexts).transform(
    (inputs): ConfiguredFieldValues =>
      Object.fromEntries(inputs.map(({ field, value }) => [field.id, value]))
  )
}

function baseArray(value: ConfiguredFieldBaseValue | undefined): string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? [...value]
    : []
}

function baseApprovalOptionIds(
  value: ConfiguredFieldBaseValue | undefined
): string[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) =>
    typeof item === 'object' &&
    item !== null &&
    'optionId' in item &&
    typeof item.optionId === 'string'
      ? [item.optionId]
      : []
  )
}

/** Keeps transformed text aligned with the value submitted through its schema. */
function normalizeOptionalText(value: null | string): null | string {
  if (value === null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Applies the resolved initial-value transformers to an existing value.
 *
 * The approval and selected-by values returned here are browser intent only;
 * the API keeps their persisted audit/state metadata when it applies a change.
 */
export function createConfiguredFieldInitialValue(
  field: ConfiguredFieldOptions,
  baseValue: ConfiguredFieldBaseValue | undefined,
  { activePayItemIds = [] }: ConfiguredFieldInitialValueContext = {}
): ConfiguredFieldValue {
  if (field.type === 'long_text') {
    let value =
      typeof baseValue === 'string' || baseValue === null ? baseValue : null
    for (const transformer of field.initialValueTransformers)
      if (transformer.enabled) value = transformer.value
    return normalizeOptionalText(value)
  }

  if (field.type === 'comments') {
    let value: null | string = null
    for (const transformer of field.initialValueTransformers)
      if (transformer.enabled) value = transformer.value
    return normalizeOptionalText(value)
  }

  if (field.type === 'selected_by') {
    let value: null | string =
      typeof baseValue === 'object' &&
      baseValue !== null &&
      !Array.isArray(baseValue) &&
      'optionId' in baseValue &&
      typeof baseValue.optionId === 'string'
        ? baseValue.optionId
        : null
    for (const transformer of field.initialValueTransformers)
      if (transformer.enabled) value = transformer.value
    return value
  }

  const activeOptionIds =
    field.type === 'pay_items'
      ? activePayItemIds
      : field.options.filter(({ deleted }) => !deleted).map(({ id }) => id)
  let value =
    field.type === 'approvals'
      ? baseApprovalOptionIds(baseValue).filter((optionId) =>
          activeOptionIds.includes(optionId)
        )
      : baseArray(baseValue)
  for (const transformer of field.initialValueTransformers) {
    if (!transformer.enabled) continue
    if (transformer.type === 'overwrite')
      value =
        transformer.value === 'all'
          ? [...activeOptionIds]
          : [...transformer.value]
  }
  return value
}
